import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  HowToReg as HowToRegIcon,
  ArrowForward as ArrowForwardIcon,
  Cancel as CancelIcon,
  Check,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Footer from '@/components/Footer';
import api from '@/services/api';

const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const ACCENT = '#10b981';
const ACCENT_DARK = '#059669';

const pill = { borderRadius: '999px', textTransform: 'none' as const, fontFamily: FONT, fontWeight: 600, px: 3.5 };

// ─── Keyframes ────────────────────────────────────────────
const STYLE_ID = 'pricing-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes pr-fade-up {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

const RevealBox: React.FC<{ children: React.ReactNode; delay?: number; sx?: object }> = ({ children, delay = 0, sx }) => {
  const { ref, visible } = useReveal();
  return (
    <Box
      ref={ref}
      sx={{
        opacity: 0,
        ...(visible && { animation: `pr-fade-up 0.7s ${delay}s ease both` }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [signupsEnabled, setSignupsEnabled] = useState<boolean | null>(null);
  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean | null>(null);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [interestEmail, setInterestEmail] = useState('');
  const [interestLoading, setInterestLoading] = useState(false);
  const [interestError, setInterestError] = useState<string | null>(null);
  const [interestSuccess, setInterestSuccess] = useState<string | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        const r = await api.get('/auth/signup-status');
        setSignupsEnabled(r.data.signupsEnabled);
        setWaitlistEnabled(r.data.waitlistEnabled ?? false);
      } catch {
        setSignupsEnabled(true);
        setWaitlistEnabled(false);
      }
    };
    check();
  }, []);

  const handleRegisterInterest = async () => {
    if (!interestEmail) { setInterestError('Please enter your email address'); return; }
    setInterestLoading(true);
    setInterestError(null);
    setInterestSuccess(null);
    try {
      const r = await api.post('/app-interest', { email: interestEmail });
      setInterestSuccess(r.data.message);
      setInterestEmail('');
    } catch (err: any) {
      setInterestError(err.response?.data?.error?.message || 'Failed to register interest. Please try again.');
    } finally {
      setInterestLoading(false);
    }
  };

  const handleCloseInterestDialog = () => {
    setInterestDialogOpen(false);
    setInterestEmail('');
    setInterestError(null);
    setInterestSuccess(null);
  };

  const handleGetStarted = () => {
    if (signupsEnabled) navigate('/register');
    else if (waitlistEnabled) setInterestDialogOpen(true);
  };

  const included = [
    'Schedule Connect with auto-sync',
    'Conflict detection',
    'Travel, directions, and weather',
    'League analytics and power rankings',
    'Works on any device',
  ];

  return (
    <Box sx={{ bgcolor: 'white', fontFamily: FONT }}>
      {/* ─── Hero ───────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 50% 30%, rgba(16,185,129,0.06) 0%, transparent 60%),
            linear-gradient(to bottom, #fafbfc 0%, white 100%)
          `,
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <RevealBox>
            <Typography
              variant="h1"
              sx={{
                fontFamily: FONT, fontWeight: 800,
                fontSize: { xs: '2.2rem', md: '3rem' },
                lineHeight: 1.1, letterSpacing: '-0.03em',
                color: '#0f172a', mb: 2,
              }}
            >
              Plans that fit your&nbsp;family
            </Typography>
          </RevealBox>
        </Container>
      </Box>

      {/* ─── Pricing card ───────────────────────────────────────── */}
      <Container maxWidth="xs" sx={{ pb: { xs: 10, md: 14 } }}>
        <RevealBox delay={0.15}>
        <Box sx={{
          bgcolor: 'white', borderRadius: '12px', p: { xs: 4, md: 5 },
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          textAlign: 'center',
        }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '2rem', color: '#0f172a', mb: 0.5 }}>
            Free
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.5rem', color: ACCENT, lineHeight: 1, mb: 1 }}>
            $0
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.95rem', color: 'text.secondary', mb: 4 }}>
            Free while in early access
          </Typography>

          <Box sx={{ textAlign: 'left', mb: 4 }}>
            {included.map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <Check sx={{ fontSize: 18, color: ACCENT }} />
                <Typography sx={{ fontFamily: FONT, fontSize: '0.95rem', color: '#1e293b' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>

          {signupsEnabled === null ? <Box sx={{ height: 52 }} /> : (
            <Button
              size="large"
              variant="contained"
              fullWidth
              endIcon={signupsEnabled ? <ArrowForwardIcon /> : undefined}
              sx={{
                ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK },
                boxShadow: `0 4px 14px ${ACCENT}40`, py: 1.5, fontSize: '1rem',
                ...(!signupsEnabled && !waitlistEnabled && {
                  bgcolor: 'grey.400', boxShadow: 'none', '&:hover': { bgcolor: 'grey.500' },
                }),
              }}
              onClick={handleGetStarted}
              disabled={!signupsEnabled && !waitlistEnabled}
            >
              {signupsEnabled ? 'Get started free' : waitlistEnabled ? 'Join the waitlist' : 'Coming soon'}
            </Button>
          )}
        </Box>
        </RevealBox>
      </Container>

      <Footer />

      {/* ─── Waitlist dialog ────────────────────────────────────── */}
      <Dialog open={interestDialogOpen} onClose={handleCloseInterestDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: FONT }}>
            <HowToRegIcon /> Join the Waitlist
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: FONT }}>
            Enter your email to join the waitlist and be notified when we're ready for you.
          </Typography>
          {interestError && <Alert severity="error" sx={{ mb: 2 }}>{interestError}</Alert>}
          {interestSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>{interestSuccess}</Alert>
          ) : (
            <TextField
              autoFocus fullWidth label="Email Address" type="email"
              value={interestEmail}
              onChange={(e) => setInterestEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !interestLoading) handleRegisterInterest(); }}
              disabled={interestLoading}
              placeholder="you@example.com"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseInterestDialog} startIcon={interestSuccess ? <Close /> : <CancelIcon />}>
            {interestSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!interestSuccess && (
            <Button
              onClick={handleRegisterInterest}
              variant="contained"
              disabled={interestLoading || !interestEmail}
              startIcon={interestLoading ? undefined : <HowToRegIcon />}
              sx={{ ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, boxShadow: 'none' }}
            >
              {interestLoading ? <CircularProgress size={20} /> : 'Join the Waitlist'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingPage;
